<template>
  <div
    id="im-background"
    :class="backgroundClasses"
    @click="clickBackground"
    role="presentation"
    data-testid="im-background"
  >
    <div id="im-modal" :class="modalClasses" @click="clickForeground" role="dialog" aria-labelledby="im-title">
      <button type="button" id="im-close-button" @click="close" aria-label="close">
        <img :src="cancelImg" role="presentation" />
      </button>
      <div id="im-copy">
        <h2 id="im-title">{{ title }}</h2>
        <p v-if="description" id="im-description">{{ description }}</p>
      </div>
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import cancelImg from "@stylesheets/assets/cancel.png";

interface InformationModalData {
  title: string;
  description?: string;
  visible?: boolean;
}

const props = withDefaults(defineProps<InformationModalData>(), {
  visible: false,
});

const emit = defineEmits(["close"]);

function clickForeground(e: MouseEvent): void {
  e.stopPropagation();
}

function close(): void {
  emit("close");
}

function clickBackground(e: MouseEvent): void {
  e.stopPropagation();
  close();
}

const modalClasses = computed(() => {
  return {
    visible: props.visible,
  };
});

const backgroundClasses = computed(() => {
  return {
    "im-background": true,
    "visible": props.visible,
  };
});
</script>

<style scoped lang="scss">
$breakpoint: 768px;

#im-background {
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;

  &.visible {
    background-color: rgba(0, 0, 0, 0.25);
    visibility: visible;
  }

  transition: background-color 0.3s ease-out 0s, visibility 0.3s ease-out 0s;
}

#im-close-button {
  @media (min-width: ($breakpoint + 1px)) {
    position: absolute;
    top: 28px;
    right: 28px;
  }

  @media (max-width: $breakpoint) {
    float: right;
  }

  background: none;
  border: none;
}

#im-modal {
  z-index: 1001;
  background-color: white;
  position: absolute;

  cursor: default;

  @media (min-width: ($breakpoint + 1px)) {
    height: 100%;
    width: 514px;
    padding: 84px 40px;
    border-radius: 24px 0px 0px 0px;
    right: 0;
    top: 0;
    transform: translateX(100%);

    &.visible {
      transform: translateX(0);
    }
  }

  @media (max-width: $breakpoint) {
    padding: 24px;
    bottom: 0;
    left: 0;
    width: 100%;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    border-radius: 24px 24px 0px 0px;
    transform: translateY(100%);

    &.visible {
      transform: translateY(0);
    }
  }

  transition: transform 0.3s ease-out 0s;

  #im-copy {
    margin-bottom: 32px;

    #im-title {
      display: block;
      font-size: 24px;
      font-weight: 700;
      color: #003f82;
    }

    #im-description {
      display: block;
      font-size: 16px;
      font-weight: 400;
      padding: 4px 0;
      color: #003f82;
      margin-bottom: 32px;
    }
  }
}
</style>
